import { Helmet } from "react-helmet-async";

/**
 * Reusable SEO component for VK Bike On Rent
 *
 * Example:
 *
 * <SEO
 *   title="Bike & Scooty Rental in Udaipur | VK Bike On Rent"
 *   description="Rent bikes and scooters in Udaipur with VK Bike On Rent."
 *   canonical="https://vkbikeonrent.in/bikes"
 *   breadcrumbs={[
 *     {
 *       name: "Home",
 *       url: "https://vkbikeonrent.in/",
 *     },
 *     {
 *       name: "Bike Rental",
 *       url: "https://vkbikeonrent.in/bikes",
 *     },
 *   ]}
 * />
 */

export default function SEO({
  title,
  description,
  canonical,

  // =========================================================
  // SOCIAL IMAGE
  // =========================================================

  image = "https://vkbikeonrent.in/vk-preview.jpg",

  imageWidth = "1200",
  imageHeight = "630",
  imageType = "image/jpeg",

  // =========================================================
  // ROBOTS
  // =========================================================

  noindex = false,

  // =========================================================
  // STRUCTURED DATA
  // =========================================================

  schema = null,

  // =========================================================
  // BREADCRUMBS
  // =========================================================

  breadcrumbs = [],

  // =========================================================
  // PAGE TYPE
  // =========================================================

  pageType = "WebPage",

  // =========================================================
  // OPEN GRAPH TYPE
  // =========================================================

  ogType = "website",
}) {
  // =========================================================
  // VALIDATION
  // =========================================================

  if (!title || !description || !canonical) {
    console.warn(
      "SEO component requires title, description and canonical."
    );
  }

  // =========================================================
  // ROBOTS
  // =========================================================

  const robotsContent = noindex
    ? "noindex, nofollow"
    : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1";

  // =========================================================
  // WEBPAGE SCHEMA
  // =========================================================

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": pageType,

    "@id": `${canonical}#webpage`,

    url: canonical,

    name: title,

    headline: title,

    description: description,

    inLanguage: "en-IN",

    isPartOf: {
      "@id": "https://vkbikeonrent.in/#website",
    },

    about: {
      "@id": "https://vkbikeonrent.in/#business",
    },

    primaryImageOfPage: {
      "@type": "ImageObject",
      url: image,
      width: Number(imageWidth),
      height: Number(imageHeight),
    },
  };

  // =========================================================
  // BREADCRUMB SCHEMA
  // =========================================================

  const breadcrumbSchema =
    breadcrumbs.length > 0
      ? {
          "@context": "https://schema.org",

          "@type": "BreadcrumbList",

          "@id": `${canonical}#breadcrumb`,

          itemListElement: breadcrumbs.map(
            (item, index) => ({
              "@type": "ListItem",

              position: index + 1,

              name: item.name,

              item: item.url,
            })
          ),
        }
      : null;

  // =========================================================
  // ADDITIONAL SCHEMAS
  // =========================================================

  const additionalSchemas = schema
    ? Array.isArray(schema)
      ? schema
      : [schema]
    : [];

  // =========================================================
  // COMBINE SCHEMAS
  // =========================================================

  const schemas = [
    webPageSchema,

    ...(breadcrumbSchema
      ? [breadcrumbSchema]
      : []),

    ...additionalSchemas,
  ];

  // =========================================================
  // RENDER
  // =========================================================

  return (
    <Helmet>

      {/* =====================================================
          HTML LANGUAGE
      ====================================================== */}

      <html lang="en-IN" />

      {/* =====================================================
          PRIMARY SEO
      ====================================================== */}

      <title>{title}</title>

      <meta
        name="description"
        content={description}
      />

      <meta
        name="robots"
        content={robotsContent}
      />

      {/* =====================================================
          CANONICAL
      ====================================================== */}

      <link
        rel="canonical"
        href={canonical}
      />

      {/* =====================================================
          OPEN GRAPH
      ====================================================== */}

      <meta
        property="og:type"
        content={ogType}
      />

      <meta
        property="og:site_name"
        content="VK Bike On Rent"
      />

      <meta
        property="og:title"
        content={title}
      />

      <meta
        property="og:description"
        content={description}
      />

      <meta
        property="og:url"
        content={canonical}
      />

      <meta
        property="og:locale"
        content="en_IN"
      />

      <meta
        property="og:image"
        content={image}
      />

      <meta
        property="og:image:secure_url"
        content={image}
      />

      <meta
        property="og:image:type"
        content={imageType}
      />

      <meta
        property="og:image:width"
        content={imageWidth}
      />

      <meta
        property="og:image:height"
        content={imageHeight}
      />

      <meta
        property="og:image:alt"
        content={`${title} - VK Bike On Rent`}
      />

      {/* =====================================================
          TWITTER / X
      ====================================================== */}

      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content={title}
      />

      <meta
        name="twitter:description"
        content={description}
      />

      <meta
        name="twitter:url"
        content={canonical}
      />

      <meta
        name="twitter:image"
        content={image}
      />

      <meta
        name="twitter:image:alt"
        content={`${title} - VK Bike On Rent`}
      />

      {/* =====================================================
          STRUCTURED DATA
      ====================================================== */}

      {schemas.map((schemaItem, index) => (
        <script
          key={`schema-${index}`}
          type="application/ld+json"
        >
          {JSON.stringify(schemaItem)}
        </script>
      ))}

    </Helmet>
  );
}